/**
 * Full-entropy random ids for widget session ids.
 *
 * 🚨 A widget session id is a CAPABILITY, not a label. Neither widget
 * WebSocket binds an id to its owner — chat resumes by id alone and the voice
 * WS accepts any id — and every verified identity (email OTP and partner
 * identity token) is stored in Redis keyed on it. Whoever holds the id holds the
 * conversation and, once verified, the buyer's orders.
 *
 * So: 128 bits from the browser's CSPRNG, and NEVER `Math.random()` — not even as
 * a fallback. `crypto.getRandomValues` is used directly rather than
 * `crypto.randomUUID`: it exists in every browser that runs this app, including
 * non-secure contexts (plain http, where `randomUUID` is undefined — which is
 * how the old `Math.random` fallback could actually be reached), and a single
 * code path leaves no rarely-taken branch to rot.
 *
 * The backend never validates id shape (api/widget.py, "THE READ PATH ACCEPTS
 * ANY ID SHAPE"), so ids already stored in browsers keep resuming unchanged.
 */

/** 32 lowercase hex chars = 128 bits from `crypto.getRandomValues`. */
export function randomHex128(): string {
    const bytes = new Uint8Array(16);
    crypto.getRandomValues(bytes);
    let hex = '';
    for (let i = 0; i < bytes.length; i++) {
        hex += bytes[i].toString(16).padStart(2, '0');
    }
    return hex;
}

/** Chat session id — `sess_` prefix kept (Tickets and the agent read it). */
export const newChatSessionId = (): string => `sess_${randomHex128()}`;

/** Voice session id — `session-` prefix kept. */
export const newVoiceSessionId = (): string => `session-${randomHex128()}`;
